import * as PIXI from 'pixi.js';

export type TierTheme = 'planet' | 'solar';

interface DensityData {
  production: number; // 0-1 normalized
  knowledge: number;
  energy: number;
  pvp: number;
  tier: number;
}

export class MapRenderer {
  app: PIXI.Application;
  private densityLayer: PIXI.Graphics;
  private overlayLayer: PIXI.Graphics;
  private nodeLayer: PIXI.Container;
  private particleContainer: PIXI.Container;
  private particles: Particle[] = [];
  private theme: TierTheme = 'planet';
  private width: number;
  private height: number;
  private ticker: PIXI.Ticker;

  // Node callbacks
  onNodeClick?: (nodeId: number) => void;

  constructor(canvas: HTMLCanvasElement) {
    this.width = canvas.clientWidth || 800;
    this.height = canvas.clientHeight || 500;

    this.app = new PIXI.Application({
      view: canvas,
      width: this.width,
      height: this.height,
      backgroundColor: 0x0a0e1a,
      antialias: true,
      resolution: window.devicePixelRatio || 1,
      autoDensity: true,
    });

    this.densityLayer = new PIXI.Graphics();
    this.overlayLayer = new PIXI.Graphics();
    this.nodeLayer = new PIXI.Container();
    this.particleContainer = new PIXI.Container();

    this.app.stage.addChild(this.densityLayer);
    this.app.stage.addChild(this.particleContainer);
    this.app.stage.addChild(this.overlayLayer);
    this.app.stage.addChild(this.nodeLayer);

    this.ticker = this.app.ticker;
    this.ticker.add(this.tick, this);
  }

  resize(w: number, h: number) {
    this.width = w;
    this.height = h;
    this.app.renderer.resize(w, h);
  }

  setTheme(theme: TierTheme) {
    this.theme = theme;
  }

  // ── Main update — called every React render cycle ───────────────────────────
  updateDensity(data: DensityData) {
    if (this.theme === 'planet') {
      this.drawPlanetMap(data);
    } else {
      this.drawSolarMap(data);
    }
    this.updateParticles(data);
  }

  updateNodes(nodes: Array<{ id: number; x: number; y: number; ownerId: string | null; controlScore: number; myId: string }>) {
    this.nodeLayer.removeChildren();

    for (const node of nodes) {
      const nx = node.x * this.width;
      const ny = node.y * this.height;
      const isOwned = node.ownerId === node.myId;
      const hasOwner = node.ownerId !== null;

      const g = new PIXI.Graphics();
      // Outer ring
      const ringColor = isOwned ? 0x4af0c4 : hasOwner ? 0xf87171 : 0x2a3a5a;
      const alpha = 0.4 + node.controlScore * 0.006;
      g.lineStyle(1.5, ringColor, alpha);
      g.drawCircle(0, 0, 10);
      // Inner fill
      g.beginFill(ringColor, isOwned ? 0.25 : 0.08);
      g.drawCircle(0, 0, 7);
      g.endFill();

      g.position.set(nx, ny);
      g.interactive = true;
      g.cursor = 'pointer';
      g.on('pointerdown', () => this.onNodeClick?.(node.id));

      // Control score label
      const text = new PIXI.Text(String(Math.round(node.controlScore)), {
        fontSize: 7,
        fill: ringColor,
        fontFamily: 'monospace',
      });
      text.anchor.set(0.5);
      text.position.set(0, 0);
      g.addChild(text);

      this.nodeLayer.addChild(g);
    }
  }

  // ── Planet Map (Tier 1) ─────────────────────────────────────────────────────
  private drawPlanetMap(data: DensityData) {
    const g = this.densityLayer;
    g.clear();
    const w = this.width;
    const h = this.height;

    // Background — deep space
    g.beginFill(0x0a0e1a);
    g.drawRect(0, 0, w, h);
    g.endFill();

    // Planet sphere gradient (approximated with filled circles)
    const cx = w * 0.5;
    const cy = h * 0.5;
    const radius = Math.min(w, h) * 0.38;

    // Base planet color
    g.beginFill(0x0d1a2e, 1);
    g.drawCircle(cx, cy, radius);
    g.endFill();

    // Production density zones (teal/cyan glow)
    if (data.production > 0) {
      const prodAlpha = Math.min(0.7, data.production * 0.8);
      this.drawDensityZone(g, cx - radius * 0.2, cy + radius * 0.15, radius * 0.6, 0x4af0c4, prodAlpha * 0.4, data.production);
    }

    // Knowledge zone (purple)
    if (data.knowledge > 0) {
      const knowAlpha = Math.min(0.6, data.knowledge * 0.7);
      this.drawDensityZone(g, cx + radius * 0.25, cy - radius * 0.3, radius * 0.4, 0xa78bfa, knowAlpha * 0.35, data.knowledge);
    }

    // Energy zone (amber)
    if (data.energy > 0) {
      const energyAlpha = Math.min(0.6, data.energy * 0.7);
      this.drawDensityZone(g, cx - radius * 0.35, cy - radius * 0.2, radius * 0.45, 0xfbbf24, energyAlpha * 0.3, data.energy);
    }

    // Influence/warfare (red tinge)
    if (data.pvp > 0.1) {
      this.drawDensityZone(g, cx + radius * 0.1, cy + radius * 0.35, radius * 0.25, 0xf87171, data.pvp * 0.2, data.pvp);
    }

    // Planet continent outlines — static decorative shapes
    this.drawContinents(g, cx, cy, radius, data.production);

    // Atmosphere glow
    const atmAlpha = 0.12 + data.production * 0.08;
    g.lineStyle(3, 0x4af0c4, atmAlpha);
    g.drawCircle(cx, cy, radius + 4);
    g.lineStyle(2, 0x4af0c4, atmAlpha * 0.4);
    g.drawCircle(cx, cy, radius + 10);

    // Saturation text overlay using overlay layer
    this.drawStatusText(data);
  }

  private drawDensityZone(
    g: PIXI.Graphics,
    x: number, y: number,
    r: number,
    color: number,
    alpha: number,
    density: number
  ) {
    const steps = Math.floor(3 + density * 4);
    for (let i = steps; i >= 1; i--) {
      const fr = (r * i) / steps;
      const fa = (alpha * i) / steps;
      g.beginFill(color, fa * 0.5);
      g.drawCircle(x, y, fr);
      g.endFill();
    }
  }

  private drawContinents(g: PIXI.Graphics, cx: number, cy: number, r: number, density: number) {
    // Stylized continent blobs — deterministic positions
    const continents = [
      { ox: 0.1, oy: -0.2, rx: 0.28, ry: 0.16 },
      { ox: -0.3, oy: 0.1, rx: 0.22, ry: 0.18 },
      { ox: 0.25, oy: 0.3, rx: 0.18, ry: 0.12 },
      { ox: -0.05, oy: 0.35, rx: 0.14, ry: 0.10 },
    ];
    const baseAlpha = 0.06 + density * 0.08;
    for (const c of continents) {
      g.beginFill(0x1a3050, baseAlpha);
      g.drawEllipse(cx + c.ox * r, cy + c.oy * r, c.rx * r, c.ry * r);
      g.endFill();
      if (density > 0.3) {
        // Industrial markers
        g.beginFill(0x4af0c4, (density - 0.3) * 0.15);
        g.drawEllipse(cx + c.ox * r, cy + c.oy * r, c.rx * r * 0.6, c.ry * r * 0.6);
        g.endFill();
      }
    }
  }

  // ── Solar Map (Tier 2) ──────────────────────────────────────────────────────
  private drawSolarMap(data: DensityData) {
    const g = this.densityLayer;
    g.clear();
    const w = this.width;
    const h = this.height;

    g.beginFill(0x040810);
    g.drawRect(0, 0, w, h);
    g.endFill();

    // Star field (static decorative dots)
    const starSeeds = [0.1, 0.23, 0.45, 0.67, 0.89, 0.12, 0.34, 0.56, 0.78, 0.9, 0.15, 0.37, 0.62, 0.84];
    for (let i = 0; i < starSeeds.length; i++) {
      const sx = (starSeeds[i] * w + i * w * 0.07) % w;
      const sy = (starSeeds[(i + 3) % starSeeds.length] * h + i * h * 0.05) % h;
      g.beginFill(0xffffff, 0.3 + (i % 3) * 0.15);
      g.drawCircle(sx, sy, 0.8);
      g.endFill();
    }

    const cx = w * 0.5;
    const cy = h * 0.5;

    // Sun (central star)
    const sunR = 22 + data.energy * 12;
    const sunGlowAlpha = 0.15 + data.production * 0.2;
    g.beginFill(0xfde68a, sunGlowAlpha * 0.3);
    g.drawCircle(cx, cy, sunR * 3);
    g.endFill();
    g.beginFill(0xfbbf24, sunGlowAlpha);
    g.drawCircle(cx, cy, sunR * 1.5);
    g.endFill();
    g.beginFill(0xfef3c7, 0.9);
    g.drawCircle(cx, cy, sunR);
    g.endFill();

    // Dyson ring progress (Unit 22 proxy via energy density)
    if (data.energy > 0.1) {
      const dysonR = sunR * 3.5;
      const arcAngle = data.energy * Math.PI * 2;
      g.lineStyle(2, 0xfbbf24, 0.5 * data.energy);
      g.arc(cx, cy, dysonR, -Math.PI / 2, -Math.PI / 2 + arcAngle);
    }

    // Orbital rings (planet orbits)
    const orbits = [0.18, 0.3, 0.45, 0.6];
    const planetColors = [0xfde68a, 0x4af0c4, 0xa78bfa, 0xf87171];
    for (let i = 0; i < orbits.length; i++) {
      const orbitR = Math.min(w, h) * orbits[i];
      g.lineStyle(0.5, 0x1a2a4a, 0.4);
      g.drawCircle(cx, cy, orbitR);

      // Planet on orbit
      const angle = (Date.now() / 1000) * 0.05 * (1 / orbits[i]); // slow drift
      const px = cx + Math.cos(i * 1.5 + 0.8) * orbitR;
      const py = cy + Math.sin(i * 1.5 + 0.8) * orbitR;
      const pR = 5 + i * 2;
      const density = i === 0 ? data.production : i === 1 ? data.knowledge : i === 2 ? data.energy : data.pvp;
      const glowA = 0.15 + density * 0.35;
      g.beginFill(planetColors[i], glowA * 0.4);
      g.drawCircle(px, py, pR * 2);
      g.endFill();
      g.beginFill(planetColors[i], 0.85);
      g.drawCircle(px, py, pR);
      g.endFill();

      // Asteroid belt effect (production density)
      if (i === 2 && data.production > 0.2) {
        this.drawAsteroidBelt(g, cx, cy, orbitR * 0.8, orbitR, data.production);
      }
    }

    // Station cluster trails (production density)
    if (data.production > 0.4) {
      const stationCount = Math.floor(data.production * 8);
      for (let s = 0; s < stationCount; s++) {
        const sa = (s / stationCount) * Math.PI * 2;
        const sr = Math.min(w, h) * 0.28;
        const sx2 = cx + Math.cos(sa) * sr;
        const sy2 = cy + Math.sin(sa) * sr;
        g.beginFill(0x4af0c4, 0.15 * data.production);
        g.drawRect(sx2 - 1.5, sy2 - 1.5, 3, 3);
        g.endFill();
      }
    }

    this.drawStatusText(data);
  }

  private drawAsteroidBelt(g: PIXI.Graphics, cx: number, cy: number, rMin: number, rMax: number, density: number) {
    const count = Math.floor(density * 30);
    const seeds = [0.1, 0.2, 0.35, 0.5, 0.65, 0.75, 0.88, 0.92, 0.05, 0.15,
      0.25, 0.4, 0.55, 0.7, 0.82, 0.95, 0.08, 0.18, 0.32, 0.48,
      0.62, 0.72, 0.85, 0.97, 0.03, 0.13, 0.28, 0.44, 0.58, 0.68];
    for (let i = 0; i < Math.min(count, seeds.length); i++) {
      const angle = seeds[i] * Math.PI * 2;
      const r = rMin + (rMax - rMin) * seeds[(i + 5) % seeds.length];
      const ax = cx + Math.cos(angle) * r;
      const ay = cy + Math.sin(angle) * r;
      g.beginFill(0x8ba0c0, 0.3);
      g.drawCircle(ax, ay, 1);
      g.endFill();
    }
  }

  private drawStatusText(data: DensityData) {
    this.overlayLayer.clear();
    // We skip text here since PIXI.Text is expensive to recreate each frame.
    // Status is shown in the React UI overlay instead.
  }

  // ── Particles ───────────────────────────────────────────────────────────────

  private updateParticles(data: DensityData) {
    // Spawn particles proportional to density
    const target = Math.floor(data.production * 40 + data.energy * 20 + data.knowledge * 15);
    while (this.particles.length < target) {
      this.particles.push(this.spawnParticle(data));
    }
    while (this.particles.length > target + 5) {
      const p = this.particles.pop()!;
      if (p.sprite.parent) this.particleContainer.removeChild(p.sprite);
      p.sprite.destroy();
    }
  }

  private spawnParticle(data: DensityData): Particle {
    const g = new PIXI.Graphics();
    const colors = [0x4af0c4, 0xa78bfa, 0xfbbf24];
    const color = colors[Math.floor(Math.random() * colors.length)];
    g.beginFill(color, 0.6);
    g.drawCircle(0, 0, 0.8 + Math.random() * 1.2);
    g.endFill();

    // Start near center
    g.position.set(
      this.width * (0.3 + Math.random() * 0.4),
      this.height * (0.3 + Math.random() * 0.4)
    );
    this.particleContainer.addChild(g);

    const angle = Math.random() * Math.PI * 2;
    const speed = 0.1 + Math.random() * 0.4;

    return {
      sprite: g,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      life: 1,
      decay: 0.003 + Math.random() * 0.005,
    };
  }

  private tick() {
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.sprite.position.x += p.vx;
      p.sprite.position.y += p.vy;
      p.life -= p.decay;
      p.sprite.alpha = p.life;

      if (p.life <= 0 || p.sprite.position.x < 0 || p.sprite.position.x > this.width ||
          p.sprite.position.y < 0 || p.sprite.position.y > this.height) {
        this.particleContainer.removeChild(p.sprite);
        p.sprite.destroy();
        this.particles.splice(i, 1);
      }
    }
  }

  destroy() {
    this.ticker.remove(this.tick, this);
    this.app.destroy(false, { children: true });
  }
}

interface Particle {
  sprite: PIXI.Graphics;
  vx: number;
  vy: number;
  life: number;
  decay: number;
}
